'use client';

import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Info, XIcon } from 'lucide-react';
import { useState } from 'react';

interface SoraVideoTooltipProps {
  planCredits: number;
  services: any[];
  className?: string;
  planName: string;
  isYearly: boolean;
}

export const SoraVideoTooltip = ({
  planCredits,
  services,
  className,
  planName,
  isYearly,
}: SoraVideoTooltipProps) => {
  const [open, setOpen] = useState(false);

  // Helper to find cost by exact slug
  const getCost = (slug: string) => {
    const service = services.find((s) => s.slug === slug);
    return service ? service.creditCost : 0;
  };

  const sora2_720p_10s = getCost('sora-2-720p-10s');
  const sora2_720p_15s = getCost('sora-2-720p-15s');

  const sora2pro_720p_10s = getCost('sora-2-pro-720p-10s');
  const sora2pro_720p_15s = getCost('sora-2-pro-720p-15s');
  const sora2pro_1080p_10s = getCost('sora-2-pro-1080p-10s');
  const sora2pro_1080p_15s = getCost('sora-2-pro-1080p-15s');

  // Calculate generic "videos" estimate using a mid-range cost (e.g., standard 10s)
  // or a range. The prompt asks for "approx. 4 to 86 videos".
  // 2600 / 30 = 86.6 -> 86
  // 2600 / 630 = 4.12 -> 4
  const minCost = Math.min(sora2_720p_10s || 30, sora2_720p_15s || 45, sora2pro_720p_10s || 150);
  const maxCost = Math.max(
    sora2pro_1080p_15s || 630,
    sora2pro_1080p_10s || 330,
    sora2pro_720p_15s || 270
  );

  const maxVideos = minCost > 0 ? Math.floor(planCredits / minCost) : 0;
  const minVideos = maxCost > 0 ? Math.floor(planCredits / maxCost) : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        className={cn('inline-flex cursor-pointer items-center justify-center', className)}
        aria-label="More information about Sora 2 videos"
        onClick={() => setOpen(true)}
      >
        <Info className="size-4 text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-white" />
      </button>
      <DialogContent
        className="z-[10000] w-[calc(100%-2rem)] max-w-[350px] border-0 bg-[#212120] p-4 pb-3 text-left text-white"
        showCloseButton={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Credit Usage Guide</DialogTitle>
        </VisuallyHidden>

        <DialogClose className="absolute top-5 right-5 z-[10001] cursor-pointer rounded-full bg-white/10 p-2 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus:outline-none">
          <XIcon className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="mb-4 flex items-center gap-2 pt-1">
          <Info className="size-4 text-gray-400" />
          <span className="font-semibold text-gray-300 text-sm uppercase tracking-wide">
            Credit Usage Guide
          </span>
        </div>

        {/* Section 1: Standard */}
        <div className="mb-4">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="font-bold text-base">Standard (Sora 2)</span>
          </div>
          <p className="mb-2 text-gray-400 text-xs">Good for quick drafts. 720p.</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>10 seconds:</span>
              <span className="font-medium text-white">{sora2_720p_10s} Credits</span>
            </div>
            <div className="flex justify-between">
              <span>15 seconds:</span>
              <span className="font-medium text-white">{sora2_720p_15s} Credits</span>
            </div>
          </div>
        </div>

        <div className="my-4 h-px bg-white/20" />

        {/* Section 2: Professional */}
        <div className="mb-4">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="font-bold text-base">Professional (Sora 2 Pro)</span>
          </div>
          <p className="mb-2 text-gray-400 text-xs">High detail. 720p or 1080p.</p>

          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span>720p (10s / 15s):</span>
              <div className="font-medium text-white">
                {sora2pro_720p_10s} <span className="mx-0.5 font-normal text-gray-500">/</span>{' '}
                {sora2pro_720p_15s} Credits
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span>1080p (10s / 15s):</span>
              <div className="font-medium text-white">
                {sora2pro_1080p_10s} <span className="mx-0.5 font-normal text-gray-500">/</span>{' '}
                {sora2pro_1080p_15s} Credits
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 border-white/20 border-t pt-3">
          <p className="text-gray-400 text-xs leading-relaxed">
            {planName} provides {minVideos} to {maxVideos} videos depending on quality.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
